/**
 * useClientToken — Find-or-create a token for a specific MCP client.
 * Matches tokens by name (case-insensitive) against the active key list.
 */

import { useState, useMemo, useCallback } from 'react';
import { authApi, type ApiKey, type ApiKeyCreateResponse } from '../../../api/auth';

export type ConnectionStatus = 'connected' | 'pending' | 'not-connected';

export interface ClientTokenState {
  /** Most recent active key matching this client name */
  existingToken: ApiKey | null;
  /** Token has been used at least once */
  isConnected: boolean;
  /** Visual status for badge */
  status: ConnectionStatus;
  /** Create a new token named after this client */
  createToken: () => Promise<void>;
  /** Reveal the matching existing key (server-side decrypt). Returns null when unrecoverable. */
  revealToken: () => Promise<string | null>;
  /** The full key string, only available right after creation */
  freshKey: string | null;
  /** Creation in progress */
  creating: boolean;
  /** Error from creation */
  error: string | null;
}

export function useClientToken(
  clientName: string,
  keys: ApiKey[],
  onKeysChanged: () => Promise<void>,
): ClientTokenState {
  const [freshKey, setFreshKey] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const existingToken = useMemo(
    () =>
      keys
        .filter((k) => !k.revokedAt && k.name.toLowerCase() === clientName.toLowerCase())
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0] ?? null,
    [keys, clientName],
  );

  const isConnected = existingToken?.lastUsedAt != null;

  const status: ConnectionStatus = existingToken
    ? isConnected
      ? 'connected'
      : 'pending'
    : 'not-connected';

  const createToken = useCallback(async () => {
    setCreating(true);
    setError(null);
    try {
      const result: ApiKeyCreateResponse = await authApi.createApiKey(clientName);
      setFreshKey(result.key);
      await onKeysChanged();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to create token');
    } finally {
      setCreating(false);
    }
  }, [clientName, onKeysChanged]);

  const revealToken = useCallback(async (): Promise<string | null> => {
    if (!existingToken?.revealable) return null;
    try {
      const res = await authApi.revealApiKey(existingToken.id);
      return res.key;
    } catch {
      return null; // pre-feature key — caller falls back to paste
    }
  }, [existingToken]);

  return { existingToken, isConnected, status, createToken, revealToken, freshKey, creating, error };
}
